<template>
  <vue-slick-carousel :class="['carousel', { 'carousel-center': centerMode }]" v-bind="options" ref="carousel">
    <slot>
      <div class="sr-only">Insert some content, so the carousel can work properly!</div>
    </slot>

    <template #prevArrow="data">
      <slot name="prev" :slotScope="data">
        <b-button variant="link" class="slick-arrow slick-arrow-prev">
          <b-icon icon="arrow-left" variant="light" scale="2"/>
          <span class="d-none">{{ data.currentSlide }} / {{ data.slideCount }}</span>
        </b-button>
      </slot>
    </template>

    <template #nextArrow="data">
      <slot name="next" :slotScope="data">
        <b-button variant="link" class="slick-arrow slick-arrow-next">
          <span class="d-none">{{ data.currentSlide }} / {{ data.slideCount }}</span>
          <b-icon icon="arrow-right" variant="light" scale="2"/>
        </b-button>
      </slot>
    </template>

    <template #customPaging="page">
      <button><div class="sr-only">{{ page}}</div></button>
    </template>
  </vue-slick-carousel>
</template>

<script>
import { BREAKPOINTS } from '@/assets/js/modules/scss-variables'

// docs: https://github.com/gs-shop/vue-slick-carousel/blob/master/docs/API.md
import VueSlickCarousel from 'vue-slick-carousel'
import { props, defaultValues } from 'vue-slick-carousel/src/defaultProps'

const BASE_CAROUSEL_OPTIONS = Object.assign({}, defaultValues, {
  accessibility: true,
  adaptiveHeight: true,
  arrows: false,
  asNavFor: null,
  autoplay: true,
  autoplaySpeed: 6000,
  centerMode: false,
  centerPadding: '-7.5px',
  cssEase: 'ease',
  dots: true,
  dotsClass: 'slick-dots',
  draggable: true,
  edgeFriction: 0.35,
  fade: false,
  focusOnSelect: false,
  infinite: true,
  initialSlide: 0,
  lazyLoad: null,
  pauseOnDotsHover: true,
  pauseOnFocus: false,
  pauseOnHover: true,
  rows: 1,
  rtl: false,
  slidesPerRow: 1,
  slidesToScroll: 1,
  slidesToShow: 1,
  speed: 400,
  swipe: 'ontouchstart' in window || window.navigator.maxTouchPoints > 0 || window.navigator.msMaxTouchPoints > 0,
  swipeToSlide: false,
  touchMove: true,
  touchThreshold: 5,
  useCSS: true,
  useTransform: true,
  variableWidth: false,
  vertical: false,
  waitForAnimate: true,
  /* use breakpoints const to define responsive options
  *  e.g.
  *  responsive: [
  *   {
  *     breakpoint: BREAKPOINTS.md.value,
  *     settings: { ... }
  *   }
  * ]
  */
  responsive: []
})

const BASE_CENTERMODE_CAROUSEL_OPTIONS = {
  adaptiveHeight: false,
  centerMode: true,
  centerPadding: '320px',
  focusOnSelect: true,
  slidesToScroll: 1,
  slidesToShow: 1,
  responsive: [
    {
      breakpoint: BREAKPOINTS.xl,
      settings: {
        centerPadding: '260px'
      }
    },
    {
      breakpoint: BREAKPOINTS.lg,
      settings: {
        centerPadding: '180px'
      }
    },
    {
      breakpoint: BREAKPOINTS.md,
      settings: {
        centerPadding: '120px'
      }
    },
    {
      breakpoint: BREAKPOINTS.sm,
      settings: {
        centerPadding: '40px'
      }
    }
  ]
}

export default {
  name: 'Carousel',
  components: {
    VueSlickCarousel
  },
  props: Object.keys(props),
  data () {
    return {
      breakpoints: BREAKPOINTS,
      options: this.getOptions()
    }
  },
  methods: {
    getOptions () {
      const inputOptions = this.$props
      const baseOptions = inputOptions.centerMode ? Object.assign({}, BASE_CAROUSEL_OPTIONS, BASE_CENTERMODE_CAROUSEL_OPTIONS) : Object.assign({}, BASE_CAROUSEL_OPTIONS)

      const inputResponsiveOptions = this.$props.responsive || []
      const baseResponsiveOptions = baseOptions.responsive.map(o => o.breakpoint)
      const unicResponsiveOptions = inputResponsiveOptions.filter(o => !baseResponsiveOptions.includes(o.breakpoint))

      // Overwrite baseOptions with inputOptions but not responsive options
      Object.keys(inputOptions)
        .forEach(oKey => {
          if (inputOptions[oKey] !== undefined && oKey !== 'responsive') baseOptions[oKey] = inputOptions[oKey]
        })

      // Overwrite baseResponsiveOptions with inputResponsiveOptions and concat with unitResponsiveOptions
      baseOptions.responsive = baseOptions.responsive
        .map(bOptions => ({ breakpoint: bOptions.breakpoint, settings: Object.assign({}, bOptions.settings, (inputResponsiveOptions.find(o => o.breakpoint === bOptions.breakpoint) || {}).settings || {}) }))
        .concat(unicResponsiveOptions)

      return baseOptions
    }
  }
}
</script>

<style lang="scss">
@import '~vue-slick-carousel/dist/vue-slick-carousel.css';

$carousel-outer-margin: $component-gap !default;
$carousel-outer-height: 80% !default;
$carousel-outer-bg: $white !default;

$carousel-list-border-radius: $leaf-border-radius-lg !default;

$carousel-list-slide-opacity: 0.25 !default;
$carousel-list-slide-current-opacity: 1 !default;

$carousel-dots-margin: $spacer 0 !default;
$carousel-dots-padding: $spacer * 0.25 !default;
$carousel-dots-width: $spacer * 1.5 !default;
$carousel-dots-height: $spacer * 0.4 !default;
$carousel-dots-bg: $gray-200 !default;
$carousel-dots-border-radius: $carousel-dots-height * 0.6 !default;
$carousel-dots-hover-bg: theme-color-level('primary', -6) !default;
$carousel-dots-active-bg: $primary !default;

$carousel-animate-margin-y: $spacer * 2 !default;
$carousel-animate-margin-x: $spacer * 2 !default;
$carousel-animate-padding-y: $carousel-list-border-radius !default;
$carousel-animate-padding-x: $carousel-list-border-radius !default;
$carousel-animate-width: 40% !default;

$carousel-animate-bg: rgba($white, 0.9) !default;
$carousel-animate-dark-bg: rgba($gray-900, 0.9) !default;
$carousel-animate-blue-bg: rgba($blueLight, 0.9) !default;
$carousel-animate-blue-btn-bg: $blue !default;
$carousel-animate-red-bg: rgba($redLight, 0.9) !default;
$carousel-animate-red-btn-bg: $red !default;
$carousel-animate-teal-bg: rgba($tealLight, 0.9) !default;
$carousel-animate-teal-btn-bg: $teal !default;

$carousel-animate-border-radius: $leaf-border-radius-sm !default;
$carousel-animate-transition-in: transform cubic-bezier(0.35, 0.05, 0.4, 1.25) 500ms 300ms, opacity ease-out 500ms 300ms !default;
$carousel-animate-transition-out: transform linear 0ms 100ms, opacity ease-in 100ms !default;

$carousel-mobile-breakpoint: $mobile-breakpoint !default;

$carousel-mobile-outer-height: 80px !default;

$carousel-mobile-dots-margin: $carousel-dots-margin !default;
$carousel-mobile-dots-padding: $spacer * 0.25 !default;
$carousel-mobile-dots-width: $spacer * 1.75 !default;
$carousel-mobile-dots-height: $spacer * 1 !default;
$carousel-mobile-dots-bg: $carousel-dots-bg !default;
$carousel-mobile-dots-border-radius: $carousel-mobile-dots-height * 0.6 !default;
$carousel-mobile-dots-hover-bg: $carousel-dots-hover-bg !default;
$carousel-mobile-dots-active-bg: $carousel-dots-active-bg !default;

.carousel {
  position: relative;
  margin-top: $carousel-outer-margin;
  margin-bottom: $carousel-outer-margin;

  .slick-arrow {
    position: absolute;
    top: 50%;
    box-shadow: none;
    transform: translateY(-50%);
    z-index: 3;

    &.slick-arrow-prev {
      left: 0;
    }

    &.slick-arrow-next {
      right: 0;
    }
  }

  .slick-list {
    @include make-leaf($carousel-list-border-radius);
    position: relative;
    overflow: hidden;
    z-index: 2;

    .slick-track {
      display: flex;
      align-items: flex-start;
    }
  }

  .slick-slide {
    height: auto;

    img {
      display: block;
      margin: 0;
      width: 100%;
      max-width: none;
    }

    > div {
      height: 100%;

      > [tabindex="-1"] {
        display: block!important;
        height: 100%;

        > * {
          margin: 0;
        }

        > .card {
          .card-img,
          .card-img-top,
          .card-img-bottom,
          .card-img-left,
          .card-img-right {
            @include reset-leaf();
          }
        }
      }
    }
  }

  .slick-dots {
    @include list-unstyled();
    display: flex!important;
    flex-wrap: wrap;
    justify-content: center;
    margin: $carousel-dots-margin;

    li {
      button {
        display: block;
        overflow: hidden;
        margin: 0;
        padding: $carousel-dots-padding;
        background: none;
        border: 0 none;
        color: transparent;

        &:before {
          display: block;
          content: '';
          margin-bottom: 1px;
          width: $carousel-dots-width;
          height: $carousel-dots-height;
          background-color: $carousel-dots-bg;
          @include make-leaf($carousel-dots-border-radius);
        }
      }

      &:hover {
        button {
          &:before {
            background-color: $carousel-dots-hover-bg;
          }
        }
      }

      &.slick-active {
        button {
          &:before {
            background-color: $carousel-dots-active-bg;
          }
        }
      }
    }
  }

  &.carousel-center {
    .slick-list {
      @include reset-leaf();

      .slick-slide {
        opacity: $carousel-list-slide-opacity;

        > div {
          @include make-col-ready();

          > [tabindex="-1"] {
            @include make-leaf($carousel-list-border-radius);
            overflow: hidden;

            &.no-leaf {
              @include reset-leaf();
            }
          }
        }

        &.slick-current {
          opacity: $carousel-list-slide-current-opacity;
        }
      }
    }
  }

  #app > main > div > &:first-child {
    &:before {
      display: block;
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      width: 100vw;
      height: $carousel-outer-height;
      background-color: $carousel-outer-bg;
      transform: translateX(-50%);
      z-index: 1;
    }
  }

  .breadcrumbnavigation + & {
    margin-top: 0;

    .slick-list {
      @include reset-leaf('top');
    }
  }

  @include media-breakpoint-down($carousel-mobile-breakpoint) {
    .slick-dots {
      margin: $carousel-mobile-dots-margin;

      li {
        button {
          padding: $carousel-mobile-dots-padding;
          width: calc(#{$carousel-mobile-dots-width} + #{$carousel-mobile-dots-padding * 2});
          height: calc(#{$carousel-mobile-dots-height} + #{$carousel-mobile-dots-padding * 2});

          &:before {
            width: $carousel-mobile-dots-width;
            height: $carousel-mobile-dots-height;
            background-color: $carousel-mobile-dots-bg;
            @include make-leaf($carousel-mobile-dots-border-radius);
          }
        }

        &:hover {
          button {
            &:before {
              background-color: $carousel-mobile-dots-hover-bg;
            }
          }
        }

        &.slick-active {
          button {
            &:before {
              background-color: $carousel-mobile-dots-active-bg;
            }
          }
        }
      }
    }

    #app > main > .container > &:first-child {
      &:before {
        height: $carousel-mobile-outer-height;
      }
    }
  }

  @include media-breakpoint-up(get-breakpoint($carousel-mobile-breakpoint, 1)) {
    .slick-list {
      .slick-slide {
        position: relative;

        .animate {
          position: absolute;
          width: $carousel-animate-width;
          padding: $carousel-animate-padding-y $carousel-animate-padding-x;
          background-color: $carousel-animate-bg;
          color: contrast-color($carousel-animate-bg);
          @include make-leaf($carousel-animate-border-radius);
          opacity: 0;
          transition: $carousel-animate-transition-out;

          &.dark {
            background-color: $carousel-animate-dark-bg;
            color: contrast-color($carousel-animate-dark-bg);
          }

          &.blue {
            background-color: $carousel-animate-blue-bg;
            color: contrast-color($carousel-animate-blue-bg);

            .btn {
              @include button-variant($carousel-animate-blue-btn-bg, $carousel-animate-blue-btn-bg);
              color: color-yiq($carousel-animate-blue-btn-bg);
            }
          }

          &.red {
            background-color: $carousel-animate-red-bg;
            color: contrast-color($carousel-animate-red-bg);

            .btn {
              @include button-variant($carousel-animate-red-btn-bg, $carousel-animate-red-btn-bg);
              color: color-yiq($carousel-animate-red-btn-bg);
            }
          }

          &.teal {
            background-color: $carousel-animate-teal-bg;
            color: contrast-color($carousel-animate-teal-bg);

            .btn {
              @include button-variant($carousel-animate-teal-btn-bg, $carousel-animate-teal-btn-bg);
              color: color-yiq($carousel-animate-teal-btn-bg);
            }
          }

          &.pos-top-left { transform-origin: top left; }
          &.pos-bottom-left { transform-origin: bottom left; }
          &.pos-top-right { transform-origin: top right; }
          &.pos-bottom-right { transform-origin: bottom right; }

          &.pos-top-left,
          &.pos-bottom-left {
            left: $carousel-animate-margin-x;
            transform: translateX(-150%) scale(0);
          }

          &:not([class*="pos-"]),
          &.pos-top-right,
          &.pos-bottom-right {
            right: $carousel-animate-margin-x;
            transform: translateX(150%) scale(0);
          }

          &.pos-top-left,
          &.pos-top-right {
            top: $carousel-animate-margin-y;
          }

          &:not([class*="pos-"]),
          &.pos-bottom-left,
          &.pos-bottom-right {
            bottom: $carousel-animate-margin-y;
          }
        }

        &.slick-active {
          .animate {
            opacity: 1;
            transform: translateX(0%) scale(1);
            transition: $carousel-animate-transition-in;
          }
        }
      }
    }
  }
}
</style>
